<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    
    <!-- Informações do Paciente -->
    <div class="row">
      <div class="col-xl-12">
        <div class="card">
          <div class="card-body">
            <div class="d-flex align-items-center">
              <div class="avatar-lg me-4">
                <span class="avatar-title rounded-circle bg-primary font-size-24">
                  {{ getInitials(patientInfo.name) }}
                </span>
              </div>
              <div class="flex-grow-1">
                <h5 class="mb-1">{{ patientInfo.name }}</h5>
                <p class="text-muted mb-0">CPF: {{ patientInfo.cpf }} | Data Nasc.: {{ formatDate(patientInfo.birthDate) }}</p>
                <p class="text-muted mb-0">Email: {{ patientInfo.email }} | Telefone: {{ patientInfo.phone }}</p>
              </div>
              <div class="flex-shrink-0">
                <router-link to="/patient/profile" class="btn btn-primary">
                  <i class="bx bx-edit me-1"></i>
                  Editar Perfil
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Cards de Estatísticas -->
    <div class="row">
      <div class="col-xl-3 col-md-6">
        <div class="card mini-stats-wid">
          <div class="card-body">
            <div class="d-flex">
              <div class="flex-grow-1">
                <p class="text-muted fw-medium">Total de Exames</p>
                <h4 class="mb-0">{{ statistics.totalExams || 0 }}</h4>
              </div>
              <div class="flex-shrink-0 align-self-center">
                <div class="mini-stat-icon avatar-sm rounded-circle bg-primary">
                  <span class="avatar-title">
                    <i class="bx bx-file-find font-size-24"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-xl-3 col-md-6">
        <div class="card mini-stats-wid">
          <div class="card-body">
            <div class="d-flex">
              <div class="flex-grow-1">
                <p class="text-muted fw-medium">Exames Este Ano</p>
                <h4 class="mb-0">{{ statistics.examsThisYear || 0 }}</h4>
              </div>
              <div class="flex-shrink-0 align-self-center">
                <div class="mini-stat-icon avatar-sm rounded-circle bg-info">
                  <span class="avatar-title">
                    <i class="bx bx-calendar font-size-24"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-xl-3 col-md-6">
        <div class="card mini-stats-wid">
          <div class="card-body">
            <div class="d-flex">
              <div class="flex-grow-1">
                <p class="text-muted fw-medium">Laudos Disponíveis</p>
                <h4 class="mb-0">{{ statistics.reportsAvailable || 0 }}</h4>
              </div>
              <div class="flex-shrink-0 align-self-center">
                <div class="mini-stat-icon avatar-sm rounded-circle bg-success">
                  <span class="avatar-title">
                    <i class="bx bx-file-doc font-size-24"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-xl-3 col-md-6">
        <div class="card mini-stats-wid">
          <div class="card-body">
            <div class="d-flex">
              <div class="flex-grow-1">
                <p class="text-muted fw-medium">Próximo Exame</p>
                <h6 class="mb-0 text-primary">{{ nextExamDate || 'Nenhum agendado' }}</h6>
              </div>
              <div class="flex-shrink-0 align-self-center">
                <div class="mini-stat-icon avatar-sm rounded-circle bg-warning">
                  <span class="avatar-title">
                    <i class="bx bx-time font-size-24"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Ações Rápidas e Exames Recentes -->
    <div class="row">
      <div class="col-xl-8">
        <div class="card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-4">
              <h4 class="card-title">Histórico de Exames</h4>
              <div>
                <button @click="exportHistory" class="btn btn-sm btn-secondary me-2">
                  <i class="bx bx-download me-1"></i>
                  Exportar
                </button>
                <router-link to="/patient/exams" class="btn btn-sm btn-primary">
                  Ver Todos
                </router-link>
              </div>
            </div>
            
            <div class="table-responsive">
              <table class="table table-nowrap mb-0">
                <thead class="table-light">
                  <tr>
                    <th>Data</th>
                    <th>Tipo de Exame</th>
                    <th>Médico</th>
                    <th>Status</th>
                    <th>Laudo</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="exam in recentExams" :key="exam.id">
                    <td>{{ formatDate(exam.date) }}</td>
                    <td>{{ exam.type }}</td>
                    <td>{{ exam.doctor }}</td>
                    <td>
                      <span :class="getStatusClass(exam.status)">
                        {{ exam.status }}
                      </span>
                    </td>
                    <td>
                      <span v-if="exam.reportAvailable" class="text-success">
                        <i class="bx bx-check"></i> Disponível
                      </span>
                      <span v-else class="text-muted">
                        <i class="bx bx-time"></i> Pendente
                      </span>
                    </td>
                    <td>
                      <div class="btn-group btn-group-sm">
                        <button @click="viewExam(exam.id)" class="btn btn-outline-primary">
                          <i class="bx bx-show"></i>
                        </button>
                        <button v-if="exam.reportAvailable" 
                                @click="downloadReport(exam.id)" 
                                class="btn btn-outline-success">
                          <i class="bx bx-download"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div class="col-xl-4">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-4">Ações Rápidas</h4>
            
            <div class="d-grid gap-2">
              <router-link to="/patient/exams/schedule" class="btn btn-primary">
                <i class="bx bx-calendar-plus me-2"></i>
                Agendar Exame
              </router-link>
              
              <router-link to="/patient/exams" class="btn btn-info">
                <i class="bx bx-file-find me-2"></i>
                Ver Todos os Exames
              </router-link>
              
              <router-link to="/patient/reports" class="btn btn-success">
                <i class="bx bx-file-doc me-2"></i>
                Meus Laudos
              </router-link>
              
              <button @click="contactSupport" class="btn btn-secondary">
                <i class="bx bx-support me-2"></i>
                Suporte
              </button>
            </div>
          </div>
        </div>

        <!-- Próximos Agendamentos -->
        <div class="card mt-4">
          <div class="card-body">
            <h5 class="card-title">Próximos Agendamentos</h5>
            <div v-if="upcomingExams.length === 0" class="text-muted text-center py-3">
              <i class="bx bx-calendar-x font-size-48 text-muted"></i>
              <p class="mt-2">Nenhum exame agendado</p>
              <router-link to="/patient/exams/schedule" class="btn btn-sm btn-primary">
                Agendar Agora
              </router-link>
            </div>
            <div v-else>
              <div v-for="exam in upcomingExams" :key="exam.id" 
                   class="border-bottom pb-3 mb-3">
                <div class="d-flex justify-content-between">
                  <div>
                    <h6 class="mb-1">{{ exam.type }}</h6>
                    <p class="text-muted mb-1">
                      <i class="bx bx-calendar me-1"></i>
                      {{ formatDateTime(exam.scheduledDate) }}
                    </p>
                    <p class="text-muted mb-0">
                      <i class="bx bx-map me-1"></i>
                      {{ exam.location }}
                    </p>
                  </div>
                  <div class="text-end">
                    <span :class="getUrgencyClass(exam.urgency)">
                      {{ exam.urgency }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Gráficos de Histórico -->
    <div class="row">
      <div class="col-xl-8">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title">Histórico de Exames por Mês</h4>
            <div id="exams-timeline-chart" style="height: 300px;"></div>
          </div>
        </div>
      </div>

      <div class="col-xl-4">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title">Tipos de Exames Realizados</h4>
            <div id="exam-types-chart" style="height: 300px;"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Informações de Saúde -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title">Dicas de Saúde</h4>
            <div class="row">
              <div class="col-md-4">
                <div class="card bg-light">
                  <div class="card-body text-center">
                    <i class="bx bx-heart font-size-48 text-danger mb-3"></i>
                    <h6>Saúde Cardiovascular</h6>
                    <p class="text-muted mb-0">Mantenha exames regulares do coração</p>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="card bg-light">
                  <div class="card-body text-center">
                    <i class="bx bx-calendar-check font-size-48 text-success mb-3"></i>
                    <h6>Exames Preventivos</h6>
                    <p class="text-muted mb-0">Agende consultas preventivas regularmente</p>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="card bg-light">
                  <div class="card-body text-center">
                    <i class="bx bx-shield-quarter font-size-48 text-primary mb-3"></i>
                    <h6>Prevenção</h6>
                    <p class="text-muted mb-0">A prevenção é o melhor remédio</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import ApexCharts from 'apexcharts';
// import { patientService } from "@/api/patients";

export default {
  page: {
    title: "Meu Portal",
    meta: [{ name: "description", content: "Portal do paciente - RadioConnect" }],
  },
  components: {
    Layout,
    PageHeader,
  },
  data() {
    return {
      title: "Meu Portal",
      items: [
        {
          text: "Paciente",
          href: "/patient",
        },
        {
          text: "Dashboard",
          active: true,
        },
      ],
      patientInfo: {},
      statistics: {},
      recentExams: [],
      upcomingExams: [],
      nextExamDate: null,
      loading: false,
    };
  },
  async created() {
    await this.loadPatientData();
  },
  methods: {
    async loadPatientData() {
      this.loading = true;
      try {
        // Simular dados do paciente enquanto não temos API real
        this.patientInfo = {
          name: "João Silva",
          cpf: "123.456.789-00",
          birthDate: "1985-03-15",
          email: "joao.silva@email.com",
          phone: "(11) 99999-9999"
        };

        this.statistics = {
          totalExams: 12,
          examsThisYear: 5,
          reportsAvailable: 8,
        };

        this.recentExams = [
          {
            id: 1,
            date: "2025-01-10",
            type: "Ressonância Magnética",
            doctor: "Dr. Carlos Mendes",
            status: "Concluído",
            reportAvailable: true
          },
          {
            id: 2,
            date: "2024-12-20",
            type: "Tomografia",
            doctor: "Dra. Ana Santos",
            status: "Concluído", 
            reportAvailable: true
          },
          {
            id: 3,
            date: "2024-11-15",
            type: "Raio-X",
            doctor: "Dr. Pedro Costa",
            status: "Concluído",
            reportAvailable: false
          }
        ];

        this.upcomingExams = [
          {
            id: 4,
            type: "Ultrassom",
            scheduledDate: "2025-01-25T10:00:00",
            location: "Clínica Central - Sala 3",
            urgency: "Normal"
          }
        ];

        this.nextExamDate = "25/01/2025";

        this.$nextTick(() => {
          this.renderCharts();
        });
      } catch (error) {
        console.error("Erro ao carregar dados do paciente:", error);
        this.$toast.error("Erro ao carregar seus dados");
      } finally {
        this.loading = false;
      }
    },

    getInitials(name) {
      return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .substring(0, 2)
        .toUpperCase();
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('pt-BR');
    },

    formatDateTime(dateString) {
      const date = new Date(dateString);
      return `${date.toLocaleDateString('pt-BR')} às ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
    },

    getStatusClass(status) {
      const classes = {
        'Concluído': 'badge bg-success',
        'Em Andamento': 'badge bg-warning',
        'Agendado': 'badge bg-info',
        'Cancelado': 'badge bg-danger'
      };
      return classes[status] || 'badge bg-secondary';
    },

    getUrgencyClass(urgency) {
      const classes = {
        'Urgente': 'badge bg-danger',
        'Prioritário': 'badge bg-warning', 
        'Normal': 'badge bg-info'
      };
      return classes[urgency] || 'badge bg-secondary';
    },

    viewExam(examId) {
      this.$router.push(`/patient/exams/${examId}`);
    },

    downloadReport(examId) {
      // Implementar download do laudo
      console.log("Download report:", examId);
      this.$toast.success("Download iniciado");
    },

    exportHistory() {
      // Implementar exportação do histórico
      console.log("Exportar histórico");
      this.$toast.success("Exportação iniciada");
    },

    contactSupport() {
      // Implementar contato com suporte
      this.$toast.info("Redirecionando para suporte...");
    },

    renderCharts() {
      if (window.ApexCharts) {
        // Gráfico timeline de exames
        const timelineOptions = {
          series: [{
            name: 'Exames',
            data: [2, 1, 3, 2, 1, 2, 1, 0, 1, 2, 1, 1]
          }],
          chart: {
            type: 'line',
            height: 300,
            toolbar: { show: false }
          },
          colors: ['#556ee6'],
          xaxis: {
            categories: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
          },
          yaxis: {
            title: { text: 'Quantidade de Exames' }
          },
          stroke: {
            curve: 'smooth',
            width: 3
          }
        };

        const timelineChart = new ApexCharts(document.querySelector("#exams-timeline-chart"), timelineOptions);
        timelineChart.render();

        // Gráfico de tipos de exames
        const typesOptions = {
          series: [4, 3, 2, 2, 1],
          chart: {
            type: 'donut',
            height: 300
          },
          labels: ['Raio-X', 'Ressonância', 'Tomografia', 'Ultrassom', 'Mamografia'],
          colors: ['#556ee6', '#34c38f', '#50a5f1', '#f1b44c', '#f46a6a']
        };

        const typesChart = new ApexCharts(document.querySelector("#exam-types-chart"), typesOptions);
        typesChart.render();
      }
    }
  },
};
</script>

<style scoped>
.mini-stats-wid {
  overflow: hidden;
}

.table th,
.table td {
  font-size: 14px;
}

.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
